import { useEffect, useRef, useState } from 'react'
import { Outlet, matchPath, useLocation } from 'react-router'
import { ErrorBoundary } from '../platform/async/ErrorBoundary'
import { focusFirstAlive } from '../platform/a11y/focusFirstAlive'
import { VerifyEmailBanner } from '../auth/VerifyEmailBanner'
import { useBreakpoint } from '../platform/responsive/useBreakpoint'
import { BottomTabs } from './BottomTabs'
import { Sidebar } from './Sidebar'
import { PageMetaProvider } from './PageMeta'
import { TopBar, PAGE_TITLE_ID } from './TopBar'
import { useNavBadges } from './useNavBadges'
import { NAV_FOOT, NAV_GROUPS, SETTINGS_NAV, SUPPORT_NAV } from './nav'
import type { NavItem } from './nav'
import './shell.css'

const KEY = 'tracke5.sidebar.collapsed'

const MAIN_ID = 'main-content'

/**
 * Storage throws SecurityError when cookies are blocked, inside a sandboxed
 * iframe, and under some enterprise policies. The plan reads it unguarded in a
 * `useState` initialiser, which puts the throw above every error boundary — a
 * white screen for a user whose only sin is blocking cookies. `ThemeProvider`
 * already carries this guard and the reasoning; this is the same hazard in the
 * same shape, so it gets the same treatment. Blocked storage should cost the
 * preference, not the session.
 */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(KEY, collapsed ? '1' : '0')
  } catch {
    // The choice still applies for this session; it just will not survive a
    // reload. Nothing worth interrupting the user over.
  }
}

export function ChromeLayout() {
  const [preferCollapsed, setPreferCollapsed] = useState(readCollapsed)
  const { pathname } = useLocation()
  const breakpoint = useBreakpoint()
  const mainRef = useRef<HTMLElement>(null)

  // Product overview §6: the sidebar is a 68px rail from 640px to 1023px and
  // full above. Collapse is a stored preference in the plan and nothing else,
  // so a user who expanded it on a desktop would get 232px of a 700px tablet
  // viewport. Below desktop the rail is forced and the toggle is not offered —
  // a control that cannot change anything is the same defect as the plan's
  // inert search button.
  const canToggle = breakpoint === 'desktop' || breakpoint === 'wide'
  const collapsed = canToggle ? preferCollapsed : true

  /**
   * Below 640px the sidebar is REPLACED, not collapsed — #109.
   *
   * §6 gives phone a bottom tab bar; the forced rail was the interim, and it
   * costs 18% of a 375px viewport permanently while clipping every label to
   * 1px. It is also 68 of the 251px the shell was offering the top bar, which
   * is most of #225's overflow.
   *
   * A swap rather than an addition: two `<nav>` landmarks both labelled Primary
   * is a worse outcome than the rail, and rendering the sidebar hidden would
   * leave its 25 links in the tab order behind the tab bar.
   */
  const phone = breakpoint === 'phone'
  // Read once here and handed down: the sidebar and the More sheet draw counts, they do not fetch them.
  const badges = useNavBadges()

  // `matchPath(..., { end: false })`, not `===`, so the breadcrumb and the
  // sidebar agree. NavLink prefix-matches, so on `/transactions/abc` the sidebar
  // marks Transactions as current while an exact comparison here made the <h1>
  // say "Not found" — two parts of the same chrome disagreeing about where the
  // user is, from the moment M6 ships a detail route.
  const match = (path: string) => matchPath({ path, end: false }, pathname) !== null

  /**
   * The breadcrumb's section, across every surface that draws a destination.
   *
   * `NAV_GROUPS` alone stopped being the answer in #205: Settings and Help
   * moved to the foot and the settings screens moved to a sub-navigation, so
   * `/settings/security` matched no group and the breadcrumb said "Not found"
   * on a screen that ships. Two parts of the same chrome disagreeing about
   * where the user is — the same failure this file already records for
   * `/transactions/abc`, one enumeration further out.
   *
   * Ordered most specific first. `SETTINGS_NAV` before `NAV_FOOT` because the
   * foot's Settings link and the sub-nav's Profile are the SAME path, and
   * "Settings / Profile" is the breadcrumb the design draws — matching the
   * foot first would yield "Settings / Settings".
   */
  const SECTIONS: { name: string; items: readonly NavItem[] }[] = [
    ...NAV_GROUPS.map((g) => ({ name: g.name, items: g.items })),
    { name: 'Settings', items: SETTINGS_NAV },
    // Help & support is a foot item but not a settings screen: filed under
    // "Settings" it read "Settings / Help & support" and, truncated on a phone,
    // "Setti…". It sits with Legal, which it links to, under "Support".
    { name: 'Support', items: [...NAV_FOOT.filter((i) => i.path === '/help'), ...SUPPORT_NAV] },
    { name: 'Settings', items: NAV_FOOT },
  ]
  const group = SECTIONS.find((s) => s.items.some((i) => match(i.path)))
  const item = group?.items.find((i) => match(i.path))

  const toggle = () => {
    const next = !preferCollapsed
    writeCollapsed(next)
    setPreferCollapsed(next)
  }

  /**
   * Put focus back when a navigation drops it — issue 127.
   *
   * `<main key={pathname}>` remounts on every navigation, so a control INSIDE
   * it that holds focus is destroyed mid-flight and focus falls to `<body>`.
   * From there the next Tab starts at the top of the document, which is the
   * 26-press journey #125 added the skip link to remove — so the gap quietly
   * undoes that fix on every in-page navigation.
   *
   * **Conditional on focus actually having been lost**, which is the whole
   * design. Moving focus unconditionally would steal it from the sidebar link
   * the user just clicked — that link lives outside `<main>` and survives the
   * remount, so it still holds focus and is exactly where a keyboard user
   * expects to be for the next arrow key.
   *
   * `<body>` is the signal because that is where the browser parks focus when
   * the holder is removed. `null` is checked too: `document.activeElement` is
   * documented as nullable and is null in a detached document.
   *
   * The first commit is exempt. Nothing has navigated yet and `activeElement`
   * is `<body>` on any fresh mount, so without the guard this fires on every
   * page load — scrolling the content and, on a keyboard-first load, painting
   * the ring nobody asked for.
   *
   * **The exemption is the PATHNAME, not a boolean latch, and that distinction
   * is the whole of it.** `main.tsx` mounts under `<StrictMode>`, which in
   * development runs each effect, cleans up, and runs it again. A latch loses
   * to that by construction: the first pass sets it and returns, the second
   * reads it as already-navigated and steals focus on a fresh page load.
   * web-tester found it against a real `npm run dev`. Seeding the ref from the
   * CURRENT render makes the second pass a no-op — the pathname has not
   * changed, so there is nothing to recover from — which is the same shape
   * `Async` uses for the same reason.
   *
   * Dev-server only: the double-invoke is stripped from a production build, so
   * nothing that ships was affected. It was still wrong on every developer's
   * machine and under every browser sweep, which run against a dev server.
   *
   * `focusFirstAlive` rather than a bare `.focus()`, which is what this was
   * first written as. `<main>` is INERT while an `Overlay` is open — `syncInert`
   * puts the attribute on every other child of `<body>` — and `.focus()` on a
   * node inside an inert subtree does nothing and throws nothing. So a
   * navigation under an open dialog left focus on `<body>`, silently, which is
   * the exact failure this effect exists to prevent and the exact shape #215
   * was filed for. Confirming the move is the repo's rule, not a nicety.
   */
  const seen = useRef(pathname)
  useEffect(() => {
    if (seen.current === pathname) return
    seen.current = pathname
    const active = document.activeElement
    if (active === null || active === document.body) focusFirstAlive(mainRef.current)
  }, [pathname])

  return (
    /* Wraps the WHOLE shell, not just `<main>`: `TopBar` is the consumer and
       the routed page is the producer, so the provider has to sit above both
       of them or the value has nowhere to travel. */
    <PageMetaProvider>
      <div className="shell">
      {/* #125. First focusable element in the document, ahead of the collapse
          button and the 25 sidebar links — otherwise reaching the page costs 26
          tab presses on every page, every time.

          It moves focus itself rather than relying on the browser to focus the
          fragment target. `preventDefault` keeps the raw <a> from pushing a
          history entry the router did not create, and `focus()` scrolls the
          element into view, so the href is what a link needs to be a link and
          the handler is what actually performs the skip. */}
      <a
        className="skip-link"
        href={`#${MAIN_ID}`}
        onClick={(e) => {
          e.preventDefault()
          mainRef.current?.focus()
        }}
      >
        Skip to main content
      </a>
      {!phone && <Sidebar collapsed={collapsed} onToggle={toggle} showToggle={canToggle} badges={badges} />}
      <div className="shell__body">
        {/* Off-nav routes — a 404, a deep link into a module with no nav entry —
            get a heading that says so rather than the plan's empty string, which
            would render an h1 with no text on every such page. */}
        <TopBar section={group?.name ?? 'TrackE5'} page={item?.label ?? 'Not found'} />
        {/* Keyed to the pathname so the 280ms entry rise of M0-R20 replays on
            navigation. A CSS animation fires on mount, and this <main> outlives
            every navigation — the Outlet swaps underneath it — so without the
            key it plays once, on first load, and never again. */}
        {/* Labelled by the breadcrumb's h1, which is the document's only one and
            sits inside the breadcrumb nav. Without this a user jumping straight
            to the main landmark lands on untitled content. */}
        {/* `tabindex="-1"` is what makes the skip link's target focusable at
            all — without it focus stays on the link and the skip is inert. -1
            rather than 0, so <main> is a focus target and not an extra stop in
            every sequential traversal of the page.

            Landing here paints Chrome's UA focus ring, which shell.css rejected
            for the 25 sidebar links partly on the grounds that it follows the OS
            colour scheme rather than the app's. That premise is wrong: the ring
            tracks `color-scheme`, which tokens.css declares per theme on `html`
            AND on `body`, so it follows `data-theme`.

            Established by experiment rather than inference — an earlier reading
            of "picks by backdrop" is also wrong, and forcing
            `color-scheme: light` on the dark app flips the ring to #005FCC while
            the body background stays rgb(13,15,9), which rules the backdrop out
            outright. Across all four app/OS combinations the ring follows the
            app: #005FCC at 5.17:1 in light, #99C8FF at 10.83:1 in dark, both
            clear of M0-R5's 3:1.

            So it stays, and whether an untokenised ring is acceptable where it
            still ships is #127 — a design call, not a defect fix. */}
        <main
          key={pathname}
          id={MAIN_ID}
          ref={mainRef}
          tabIndex={-1}
          // `--tabbed` reserves the fixed bar's height. Without it the last
          // rows of every phone screen sit under the bar — in the DOM,
          // scrollable to, and covered.
          className={`enter shell__main${phone ? ' shell__main--tabbed' : ''}`}
          aria-labelledby={PAGE_TITLE_ID}
        >
          {/* #95. A throw under the outlet would otherwise take the sidebar and
              the top bar with it — measured on DataGrid as a blank document.
              The shell is precisely what a user needs in order to leave a
              broken page, so it must outlive one. Keyed by pathname with the
              main element, so navigating away clears a caught error rather than
              stranding the user on it. */}
          {/* M1-R4's reminder, a SIBLING of the outlet and never a wrapper of
              it: verification is not a gate, and the shape is what guarantees
              that rather than a branch somebody could invert later.

              Inside `<main>` rather than above it, against the plan's line.
              Above it the banner would sit outside the landmark a keyboard user
              skips to — so the reminder would be the one thing the skip link
              skips — and it would need its own copy of `.shell__main`'s 22px
              padding to line up with the page, which is a number that then
              drifts.

              Its own boundary, with `fallback={null}`: a reminder that cannot
              render must not take the page with it. #95's rule, and the same
              treatment `AppTexture` gets in main.tsx. */}
          <ErrorBoundary name="Email verification reminder" fallback={null}>
            <VerifyEmailBanner />
          </ErrorBoundary>
          <ErrorBoundary name={item?.label ?? 'This page'}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      {/* Outside `.shell__body`, because it is fixed to the viewport rather
          than laid out in the body column — and after `<main>` in the DOM so
          a keyboard user reaches the page content before the navigation, which
          is the whole reason the skip link exists at the top. */}
      {phone && <BottomTabs badges={badges} />}
      </div>
    </PageMetaProvider>
  )
}
